import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, FileJson, Download, AlertTriangle, CheckCircle2, Wand2, ClipboardPaste } from "lucide-react";
import { getBlockType, getBlockColors } from "./blocks.data";
import { parseContentJson, CONTENT_FORMAT_SAMPLE } from "./content-import";

const SAMPLE_JSON = JSON.stringify(CONTENT_FORMAT_SAMPLE, null, 2);

const downloadFormatSample = () => {
  const blob = new Blob([SAMPLE_JSON], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "kb-content-format.json";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const BlockChip = ({ block, index }) => {
  const meta = getBlockType(block.type);
  const colors = getBlockColors(block.type);
  const Icon = meta.icon;
  const summary =
    block.type === "bulletList" || block.type === "numberedList"
      ? `${block.data.items.length} items`
      : block.type === "gallery"
      ? `${block.data.images.length} images`
      : block.type === "image"
      ? block.data.alt || "no alt text"
      : block.type === "note"
      ? block.data.title || block.data.noteType
      : (block.data.text || "").replace(/<[^>]+>/g, " ").trim();

  return (
    <div className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg bg-surface_alt">
      <span className="flex-shrink-0 w-5 text-[10px] font-bold text-text_muted text-right">{index + 1}</span>
      <div className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center ${colors.bg}`}>
        <Icon size={12} className={colors.text} />
      </div>
      <span className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wide ${colors.text}`}>
        {meta.label}
      </span>
      <span className="text-[11.5px] text-text_muted truncate">{summary}</span>
    </div>
  );
};

// Paste-in importer for content JSON produced outside the builder.
// Parsing is live so the author sees what will land before committing,
// and every automatic correction is listed rather than applied silently.
const ImportContentDialog = ({ isOpen, onClose, onImport, existingCount }) => {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("replace");
  const overlayRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setText("");
      setMode(existingCount > 0 ? "append" : "replace");
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen, existingCount]);

  // The builder sits inside a FullScreenDialog that closes on Escape from its
  // own document listener. Capture the key here first and stop it, so Escape
  // closes this dialog only instead of discarding the whole article.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isOpen, onClose]);

  const result = useMemo(() => parseContentJson(text), [text]);
  const isEmpty = !text.trim();
  const canImport = !isEmpty && !result.error && result.blocks.length > 0;

  const handlePasteFromClipboard = async () => {
    try {
      const clip = await navigator.clipboard.readText();
      if (clip) setText(clip);
    } catch {
      textareaRef.current?.focus();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      onClick={(e) => e.target === overlayRef.current && onClose()}
      className="fixed inset-0 z-[2050] flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-3xl max-h-[88vh] flex flex-col rounded-2xl border border-border bg-surface shadow-2xl overflow-hidden">
        <div className="flex-shrink-0 flex items-center gap-3 px-5 py-3.5 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-primary_tint flex items-center justify-center">
            <FileJson size={16} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-bold text-text_primary">Import Content JSON</p>
            <p className="text-[11px] text-text_muted">
              Paste a content payload and it becomes editable blocks
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-text_muted" title="Close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11.5px] font-semibold text-text_primary">Content JSON</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePasteFromClipboard}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border text-[11px] font-semibold text-text_secondary"
              >
                <ClipboardPaste size={12} />
                Paste
              </button>
              <button
                type="button"
                onClick={downloadFormatSample}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border text-[11px] font-semibold text-text_secondary"
                title="Download a reference payload showing every block type"
              >
                <Download size={12} />
                Format
              </button>
            </div>
          </div>

          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            rows={12}
            placeholder={'{\n  "contents": [\n    { "type": "HEADING", "content": { "text": "Section", "level": 2, "align": "left" } }\n  ]\n}'}
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 font-mono text-[12px] text-text_primary resize-y outline-none focus:border-primary"
          />

          {isEmpty && (
            <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl bg-surface_alt border border-border">
              <FileJson size={14} className="flex-shrink-0 mt-0.5 text-text_muted" />
              <div className="text-[12px] text-text_secondary leading-relaxed">
                <p className="font-semibold text-text_primary mb-0.5">Accepted shapes</p>
                <p>
                  A bare array of blocks, or an object with a <code className="font-mono">contents</code> array.
                  Both the API create body and an exported article payload work as-is. Common near-misses are
                  repaired automatically and listed before you import.
                </p>
              </div>
            </div>
          )}

          {!isEmpty && result.error && (
            <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl bg-danger_bg border border-danger">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-danger" />
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-danger">{result.error}</p>
                <p className="text-[11.5px] text-text_secondary mt-1">
                  Download the format reference to see the exact shape every block type expects.
                </p>
                <button
                  type="button"
                  onClick={downloadFormatSample}
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-surface border border-border text-[11.5px] font-semibold text-text_primary"
                >
                  <Download size={12} />
                  Download JSON format
                </button>
              </div>
            </div>
          )}

          {canImport && (
            <>
              <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-success_bg border border-success_border">
                <CheckCircle2 size={14} className="flex-shrink-0 text-success" />
                <p className="text-[12.5px] font-semibold text-success">
                  {result.blocks.length} block{result.blocks.length > 1 ? "s" : ""} ready to import
                </p>
              </div>

              {result.repairs.length > 0 && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl bg-warning_bg border border-warning_border">
                  <Wand2 size={14} className="flex-shrink-0 mt-0.5 text-warning" />
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-warning mb-1">
                      Auto-corrected {result.repairs.length} issue{result.repairs.length > 1 ? "s" : ""}
                    </p>
                    <ul className="space-y-0.5 text-[11.5px] text-text_secondary list-disc pl-4">
                      {result.repairs.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-[11.5px] font-semibold text-text_primary">Preview</p>
                <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                  {result.blocks.map((b, i) => (
                    <BlockChip key={b.id} block={b} index={i} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-t border-border bg-surface_alt">
          {existingCount > 0 ? (
            <div className="flex items-center gap-1 p-0.5 rounded-lg bg-surface border border-border">
              {[
                { key: "append", label: `Add after ${existingCount}` },
                { key: "replace", label: "Replace all" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setMode(opt.key)}
                  className={`px-2.5 py-1.5 rounded-md text-[11.5px] font-semibold ${
                    mode === opt.key ? "bg-primary text-white" : "text-text_secondary"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 rounded-lg border border-border text-[12.5px] font-semibold text-text_secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canImport}
              onClick={() => {
                onImport(result.blocks, mode);
                onClose();
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-semibold text-white bg-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileJson size={13} />
              Import{canImport ? ` ${result.blocks.length}` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImportContentDialog;
